import React, { Component } from "react";
import API from "../utils/api";
import { toast } from "react-toastify";
import Select from "react-select";
import { ROLES } from "../utils/constants";

class TeamsPage extends Component {
  state = {
    teams: [],
    executives: [],
    teamUsers: [],
    loading: true,
    showCreateForm: false,
    newTeamName: "",
    newTeamCoordinator: null,
    editingTeam: null,
    selectedUsers: [],
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ loading: true });
    try {
      const [teamsRes, execsRes, teamUsersRes] = await Promise.all([
        API.get("/teams"),
        API.get("/executives"),
        API.get("/team-users")
      ]);

      this.setState({
        teams: teamsRes.data.data || [],
        executives: execsRes.data.data || [],
        teamUsers: teamUsersRes.data.data || [],
        loading: false
      });
    } catch (e) {
      toast.error("Failed to load data");
      this.setState({ loading: false });
    }
  };

  handleCreateTeam = async (e) => {
    e.preventDefault();
    const { newTeamName, newTeamCoordinator } = this.state;
    if (!newTeamName || !newTeamCoordinator) {
      return toast.error("Please enter a name and select a coordinator");
    }

    try {
      await API.post("/team", {
        name: newTeamName,
        coordinator_id: newTeamCoordinator.value
      });
      toast.success("Team created successfully");
      this.setState({ showCreateForm: false, newTeamName: "", newTeamCoordinator: null });
      this.fetchData();
    } catch (e) {
      toast.error("Failed to create team");
    }
  };

  openEditTeam = (team) => {
    // find users in this team
    const currentUsers = this.state.teamUsers
      .filter(tu => tu.team_id === team.id)
      .map(tu => ({ label: tu.name, value: tu.user_id }));

    this.setState({ editingTeam: team, selectedUsers: currentUsers });
  };

  handleUpdateTeamUsers = async (e) => {
    e.preventDefault();
    const { editingTeam, selectedUsers } = this.state;
    try {
      const usersList = selectedUsers || [];
      await API.post("/team/users", {
        team_id: editingTeam.id,
        user_ids: usersList.map(u => u.value)
      });
      toast.success("Team members updated");
      this.setState({ editingTeam: null, selectedUsers: [] });
      this.fetchData();
    } catch (e) {
      toast.error("Failed to update team members");
    }
  };

  handleSetCoordinator = async (teamId, coordinatorId) => {
    try {
      await API.post("/team/coordinator", { team_id: teamId, coordinator_id: coordinatorId });
      toast.success("Coordinator updated");
      this.fetchData();
    } catch (e) {
      toast.error("Failed to update coordinator");
    }
  };

  handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Are you sure you want to delete this team?")) return;
    try {
      await API.post("/team/delete", { team_id: teamId });
      toast.success("Team deleted successfully");
      this.fetchData();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || "Failed to delete team");
    }
  };

  render() {
    const { teams, executives, teamUsers, loading, showCreateForm, editingTeam, newTeamName, newTeamCoordinator, selectedUsers } = this.state;

    const userOptions = executives.map(e => ({ label: e.name, value: e.id }));

    const assignedUserIds = teamUsers.map(tu => Number(tu.user_id));
    const assignedCoordinators = teams.map(t => Number(t.coordinator_id)).filter(Boolean);

    const usersList = selectedUsers || [];
    // deduplicate executives in case of multiple roles
    const uniqueExecutives = executives.filter((e, idx, arr) => arr.findIndex(t => t.id === e.id) === idx);

    const availableMembers = uniqueExecutives.filter(e => {
      if (editingTeam && usersList.find(su => su.value === e.id)) {
        return true;
      }
      return !assignedUserIds.includes(Number(e.id)) && !assignedCoordinators.includes(Number(e.id)) && (e.role_id === ROLES.EXECUTIVE || e.role_id === ROLES.TEAM_COORDINATOR);
    });
    const memberOptions = availableMembers.map(e => ({ label: e.name, value: e.id }));

    // For coordinators, we show users who are either already the coordinator of this team,
    // or who are not assigned to any team (as member or coordinator) and are executives/coordinators.
    const getCoordinatorOptions = (currentCoordinatorId) => {
      return uniqueExecutives.filter(e => {
        if (currentCoordinatorId && e.id === currentCoordinatorId) return true;
        return !assignedUserIds.includes(Number(e.id)) && !assignedCoordinators.includes(Number(e.id)) && (e.role_id === ROLES.EXECUTIVE || e.role_id === ROLES.TEAM_COORDINATOR);
      }).map(e => ({ label: e.name, value: e.id }));
    };

    return (
      <div className="container-fluid p-4" style={{ backgroundColor: "#ecf0f5", minHeight: "100vh" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3>Manage Teams</h3>
          <button className="btn btn-primary" onClick={() => this.setState({ showCreateForm: !showCreateForm })}>
            {showCreateForm ? "Cancel" : "Create Team"}
          </button>
        </div>

        {showCreateForm && (
          <div className="card mb-4">
            <div className="card-body">
              <form onSubmit={this.handleCreateTeam} className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label>Team Name</label>
                  <input type="text" className="form-control" value={newTeamName} onChange={e => this.setState({ newTeamName: e.target.value })} required />
                </div>
                <div className="col-md-4">
                  <label>Coordinator</label>
                  <Select
                    options={getCoordinatorOptions(null)}
                    value={newTeamCoordinator}
                    onChange={val => this.setState({ newTeamCoordinator: val })}
                    placeholder="Select Coordinator"
                  />
                </div>
                <div className="col-md-4">
                  <button type="submit" style={{ marginTop: "2.7rem" }} className="btn btn-success">Save Team</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editingTeam && (
          <div className="card mb-4">
            <div className="card-body">
              <h5>Edit Members for {editingTeam.name}</h5>
              <form onSubmit={this.handleUpdateTeamUsers} className="mt-3">
                <div className="mb-3">
                  <label>Team Members</label>
                  <Select
                    isMulti
                    options={memberOptions}
                    value={selectedUsers}
                    onChange={val => this.setState({ selectedUsers: val || [] })}
                    placeholder="Select team members..."
                  />
                </div>
                <br />
                <button type="submit" className="btn btn-primary me-2">Update Members</button>
                <button type="button" className="btn btn-secondary" onClick={() => this.setState({ editingTeam: null })}>Cancel</button>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="p-4 text-center">Loading...</div>
            ) : (
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Team Name</th>
                    <th>Coordinator</th>
                    <th>Members Count</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map(team => {
                    const memberCount = this.state.teamUsers.filter(tu => tu.team_id === team.id).length;
                    return (
                      <tr key={team.id}>
                        <td>{team.id}</td>
                        <td>{team.name}</td>
                        <td>
                          <Select
                            options={getCoordinatorOptions(team.coordinator_id)}
                            value={getCoordinatorOptions(team.coordinator_id).find(o => o.value === team.coordinator_id) || { label: team.coordinator_name || 'Set Coordinator', value: team.coordinator_id }}
                            onChange={val => this.handleSetCoordinator(team.id, val.value)}
                            placeholder="Set Coordinator"
                            styles={{ container: base => ({ ...base, width: 200 }) }}
                          />
                        </td>
                        <td>{memberCount}</td>
                        <td>
                          <button className="btn btn-sm btn-info text-white me-2" onClick={() => this.openEditTeam(team)}>
                            Manage Members
                          </button>
                          <button
                            className="btn btn-sm btn-danger text-white"
                            disabled={memberCount > 0}
                            onClick={() => this.handleDeleteTeam(team.id)}
                            title={memberCount > 0 ? "Cannot delete team with active members" : "Delete Team"}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {teams.length === 0 && (
                    <tr><td colSpan="5" className="text-center">No teams found</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default TeamsPage;
